import {gql, useQuery} from '@apollo/client';
import React from 'react';
import {LoadingSpinner, Paths, SimpleDropdown, ErrorDisplay} from "../../../common";
import {SortOrder} from "../domain";
import {
    SubmittedRequestColumn,
    SubmittedRequestInfo,
} from "./SubmittedRequestDomain";
import {RelayPagination, useRelayPagination} from "../../pagination";
import {CaretDownIcon, CaretUpIcon, Icon, Pane, Text} from "evergreen-ui";
import {SubmittedRequestPreview} from "./SubmittedRequestPreview";
import {asQueryParam, SubmittedRequestListFilter} from "./SubmittedRequestListFilter";
import {SubmittedRequestListFilterBadge} from "./SubmittedRequestListFilterBadge";
import {
    SUBMITTED_REQUEST_QUERY,
    SubmittedRequestQueryResponse,
    SubmittedRequestListProps,
} from "./SubmittedRequestList";

export const SubmittedRequestClientProfileListColumn: Readonly<SubmittedRequestColumn[]> = [
    {name: 'Request Type', attr: 'requestType'},
    {name: 'Request Number', attr: 'caseNumber'},
    {name: 'Submitted Date', attr: 'submittedDate'},
    {name: 'Submitted By', attr: 'submitterName'},
    {name: 'Status', attr: 'status'},
    {name: 'Closed Date', attr: 'closedDate'},
] as const;

export const SubmittedRequestClientProfileList = ({
    selectedRequest,
    sortBy,
    pagination,
    dispatch,
    filterValue,
    searchText,
    pageSize
}: SubmittedRequestListProps) => {
    const {loading, data, error} = useQuery<SubmittedRequestQueryResponse>(
        SUBMITTED_REQUEST_QUERY,
        {
            variables: {
                ...pagination.toGraphQlParams(),
                clientGuid: filterValue.clientGuid,
                authorizationGuid: filterValue.authorizationGuid,
                search: searchText,
                params: {
                    ...asQueryParam(filterValue),
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            },
            fetchPolicy: "no-cache"
        }
    );

    const setSortByColumn = (newSortColumn: Paths<SubmittedRequestInfo>) => {
        // ignore sort by service types
        if (['NONE'].includes(newSortColumn)) {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    let totalElements: number = 0;
    if (data?.caseSubmitRequestQuery?.pageInfo?.endCursor !== null) {
        totalElements = data?.caseSubmitRequestQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalElements);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading submitted requests" />;
    }

    return (
        <Pane className="h-full relative bg-white shadow-md flex flex-col">
            <div className="flex-1 min-h-0 pt-2 pb-5 bg-white shadow-md flex flex-col">
                <div className="flex-1 min-h-0 flex flex-col w-full relative">
                    <div className="flex justify-between items-start gap-4 px-5">
                        <SubmittedRequestListFilterBadge className="pl-0 flex-1"
                                                         filter={filterValue} dispatch={dispatch} />
                        <div className="flex items-center shrink-0">
                            <SubmittedRequestListFilter initFilterValue={filterValue} dispatch={dispatch}
                                                        preserveContextFields={['clientGuid']}/>
                            <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                            minWidth={"2rem"}
                                            textColor={"gray-700"}
                                            setSelected={(newPageSize) => dispatch({
                                                type: "SET_PAGE_SIZE",
                                                pageSize: parseInt(newPageSize)
                                            })}/>
                            <RelayPagination pageInfo={data?.caseSubmitRequestQuery?.pageInfo} count={totalElements}
                                             onClick={onPaginationClick}/>
                        </div>
                    </div>

                    <div className="overflow-auto flex flex-col flex-grow min-h-0 mb-4 pt-4 pb-8 scrollbar-always">
                        <table className="table-fixed w-full">
                            <thead>
                            <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                                {
                                    SubmittedRequestClientProfileListColumn.map((column, index) => (
                                        <th key={column.attr} className={'font-light' + (index === 0 ? ' pl-8' : '')}
                                            onClick={() => setSortByColumn(column.attr)}>
                                            <div className='flex items-center'>
                                                <div>
                                                    {column.name}
                                                </div>
                                                <div className='w-6 h-6 pl-2'>
                                                    {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                        <Icon className='align-middle' icon={CaretUpIcon}/>}
                                                    {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                        <Icon className='align-middle' icon={CaretDownIcon}/>}
                                                </div>
                                            </div>
                                        </th>
                                    ))
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {
                                !loading && data?.caseSubmitRequestQuery.edges.map(({node}) => (
                                    <SubmittedRequestPreview
                                        key={node.guid}
                                        request={node}
                                        isSelected={selectedRequest?.guid === node.guid}
                                        onClick={() => dispatch({type: 'SELECT', payload: node})}
                                        columns={SubmittedRequestClientProfileListColumn}
                                    />
                                ))
                            }
                            </tbody>
                        </table>
                        {
                            loading &&
                            <div className="w-full h-full flex flex-grow items-center">
                                <LoadingSpinner spacing={20} height={"100%"}/>
                            </div>
                        }
                        {
                            totalElements === 0 && !loading && <div className="w-full h-full flex flex-grow items-center">
                                <div className="m-auto p-10">
                                    No requests matched the searching criteria.
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </Pane>
    );
};
